import { useState, useRef, useEffect, useCallback } from "react";
import { MapPin } from "lucide-react";

export interface DachCity {
  name: string;
  postalCode: string;
  country: "DE" | "AT" | "CH";
  flag: string;
}

interface LocationPickerProps {
  value: DachCity | null;
  onChange: (city: DachCity | null) => void;
}

export default function LocationPicker({ value, onChange }: LocationPickerProps) {
  const [query, setQuery] = useState(value ? value.name : "");
  const [results, setResults] = useState<DachCity[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const searchCities = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const res = await fetch(
        `${supabaseUrl}/functions/v1/location-search?q=${encodeURIComponent(q.trim())}`,
        { headers: { Authorization: `Bearer ${supabaseKey}`, apikey: supabaseKey } }
      );
      if (res.ok) {
        const cities: DachCity[] = await res.json();
        setResults(cities);
        setOpen(cities.length > 0);
        setHighlightIndex(-1);
      }
    } catch (err) {
      console.error("Location search error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInput = useCallback((val: string) => {
    setQuery(val);
    onChange(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchCities(val), 300);
  }, [onChange, searchCities]);

  const selectCity = useCallback((city: DachCity) => {
    onChange(city);
    setQuery(city.name);
    setOpen(false);
    setResults([]);
  }, [onChange]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && highlightIndex >= 0) {
      e.preventDefault();
      selectCity(results[highlightIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <label htmlFor="inquiry-location" className="text-micro block mb-2">Standort *</label>
      <div className="relative">
        {value ? (
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg" aria-hidden="true">
            {value.flag}
          </span>
        ) : (
          <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        )}
        <input
          id="inquiry-location"
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls="location-listbox"
          autoComplete="off"
          value={query}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          onKeyDown={handleKeyDown}
          className="w-full h-12 pl-11 pr-4 border border-border bg-background text-body focus:border-primary transition-colors"
          placeholder="Ort oder PLZ eingeben"
        />
        {loading && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-primary rounded-full animate-spin" />
          </div>
        )}
      </div>

      {value && (
        <p className="text-xs text-muted-foreground mt-1.5">
          {value.flag} {value.name} · PLZ {value.postalCode}
        </p>
      )}

      {open && results.length > 0 && (
        <ul
          id="location-listbox"
          role="listbox"
          className="absolute z-50 left-0 right-0 mt-1 border border-border bg-card shadow-lg max-h-64 overflow-y-auto"
        >
          {results.map((city, i) => (
            <li
              key={`${city.country}-${city.postalCode}-${city.name}`}
              role="option"
              aria-selected={highlightIndex === i}
              className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                highlightIndex === i
                  ? "bg-accent/10 text-foreground"
                  : "hover:bg-muted/50"
              }`}
              onMouseEnter={() => setHighlightIndex(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                selectCity(city);
              }}
            >
              <span className="text-lg shrink-0">{city.flag}</span>
              <div className="min-w-0">
                <p className="text-body font-medium truncate">{city.name}</p>
                <p className="text-xs text-muted-foreground">PLZ {city.postalCode}</p>
              </div>
            </li>
          ))}
        </ul>
      )}

      {query.length >= 2 && !loading && results.length === 0 && !value && (
        <p className="text-xs text-destructive mt-1.5">
          Kein Ort im DACH-Raum gefunden. Bitte prüfen Sie die Schreibweise.
        </p>
      )}
    </div>
  );
}
